import React, { useEffect, useState } from 'react';
import { RiAlertLine, RiCheckboxCircleLine, RiTruckLine, RiInformationLine } from 'react-icons/ri';
import Modal from '../ui/Modal';
import { getLocalDateString } from '../../utils/dateUtils';
import LoadingButton from '../ui/LoadingButton';
import { cn } from '../../utils/cn';

/**
 * ถาม "ของกลับแล้วหรือยัง" ตอนยกเลิกการเช่า
 *
 * ทำไมต้องถามตรงนี้: ตั้งแต่ปิดรู L1 (2026-09-08) การยกเลิกสัญญาไม่ปล่อยของกลับคลังเอง
 * ของจะกลับคลังเมื่อมีคนบันทึกรับคืนเท่านั้น — และข้อมูล prod บอกว่าหน้า "ติดตามการคืนอุปกรณ์"
 * ถูกเปิดแค่ 2 ครั้งใน 90 วัน และ **ไม่เคยมีใครปิดใบตามเก็บเลยแม้แต่ใบเดียว** (43 ใบ COMPLETED 0)
 * ถ้าบังคับให้ไปปิดที่หน้านั้น ของจะไม่กลับเข้าคลังอีกเลย → ต้องให้คนที่กดยกเลิกตอบได้ตรงนี้
 *
 * ไม่ preselect โดยตั้งใจ: บทเรียน L5 — ตัวเลือกที่ติ๊กไว้ล่วงหน้าคือทางที่ผู้ใช้เดินโดยไม่อ่าน
 * ถ้าเลือกผิดฝั่ง "ของกลับแล้ว" จะกลายเป็นปล่อยเช่าซ้อน (บั๊กเดิมที่พิสูจน์แล้วว่าเกิดจริง)
 *
 * 🔴 **ตัวตัดสินจริงคือ `rentals.delivery_date` ไม่ใช่ตัวเลือกที่กด** (BUG-UI-08) —
 * `crud/rental._goods_were_delivered()` อ่านฟิลด์นี้: ว่าง = ถือว่าของยังอยู่ในคลัง →
 * **ยกเลิกแล้วปล่อยของกลับเป็น "พร้อมใช้งาน" ทันที ไม่เปิดใบตามเก็บ ไม่ว่าจะเลือกอะไร**
 * (พิสูจน์ด้วยคู่เทียบ: rental 2592 มีวันส่งมอบ → เครื่องคง RENTED + มีใบตามเก็บ ·
 *  rental 2593 ไม่มี → เครื่อง AVAILABLE ทันที ทั้งที่กดตัวเลือกเดียวกัน)
 *
 * ตรรกะ backend ถูกแล้วและมีเหตุผล (ของที่ยังไม่เคยออกจากคลัง ไม่มีอะไรให้ตามเก็บ) —
 * ที่ผิดคือจอ ที่เสนอตัวเลือกซึ่งระบบไม่ได้เอาไปใช้ · **สัญญาที่มาจากใบงานเตรียมเครื่อง
 * มี `delivery_date` = NULL เสมอ** (โมดัล convert ไม่มีช่องวันส่งมอบ) → เป็นกลุ่มเสี่ยงที่สุด
 * จอจึงต้องให้เติมวันส่งมอบตรงนี้ก่อน แทนที่จะยอมให้เลือกตัวเลือกที่เป็นเท็จ
 */
export type GoodsCondition = 'GOOD' | 'DAMAGED' | 'LOST';

interface CancelGoodsCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalNumber: string;
  /** จำนวนอุปกรณ์ที่ยังไม่ได้รับคืนของสัญญานี้ (0 = ไม่มีของให้ถาม) */
  outstandingCount: number;
  /** `rentals.delivery_date` ปัจจุบัน — null/ว่าง = ระบบถือว่าของยังอยู่ในคลัง */
  deliveryDate?: string | null;
  /** มี `rentals.edit` ไหม — ต้องมีถึงจะบันทึกวันส่งมอบจากในโมดัลนี้ได้ (PUT /rentals/{id}) */
  canEditRental?: boolean;
  onConfirm: (answer: {
    equipment_returned: boolean;
    equipment_condition?: GoodsCondition;
    /** ผู้ใช้เพิ่งกรอกวันส่งมอบย้อนหลัง — หน้าเรียกต้องบันทึกก่อนแล้วค่อยยกเลิก */
    delivery_date?: string;
  }) => Promise<void>;
}

const CONDITIONS: Array<{ value: GoodsCondition; label: string }> = [
  { value: 'GOOD', label: 'สภาพดี' },
  { value: 'DAMAGED', label: 'ชำรุด' },
  { value: 'LOST', label: 'สูญหาย' },
];

const CancelGoodsCheckModal: React.FC<CancelGoodsCheckModalProps> = ({
  isOpen,
  onClose,
  rentalNumber,
  outstandingCount,
  deliveryDate,
  canEditRental = false,
  onConfirm,
}) => {
  const [answer, setAnswer] = useState<'returned' | 'with_customer' | null>(null);
  const [condition, setCondition] = useState<GoodsCondition>('GOOD');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // วันส่งมอบที่ผู้ใช้กรอกย้อนหลังในโมดัลนี้ (เฉพาะกรณีสัญญายังไม่มี)
  const [backfillDate, setBackfillDate] = useState('');

  // วันส่งมอบย้อนหลังต้องไม่อยู่ในอนาคต (BUG-S2186-001) — `max` ของ input เป็นแค่คำแนะนำ
  // เบราว์เซอร์ไม่บล็อกค่าที่พิมพ์เกิน ต้องตรวจเองก่อนส่ง · ใช้วันท้องถิ่น ไม่ใช่ UTC
  const todayLocal = getLocalDateString();
  const backfillInFuture = backfillDate !== '' && backfillDate > todayLocal;
  const hasDeliveryDate = Boolean(deliveryDate) || (backfillDate !== '' && !backfillInFuture);
  // ตัวเลือก "ของยังอยู่กับลูกค้า" จะเป็นจริงได้ก็ต่อเมื่อระบบรู้ว่าเคยส่งมอบ
  const withCustomerAvailable = hasDeliveryDate;

  // ผู้ใช้เลือก "ยังอยู่กับลูกค้า" ไว้ก่อน แล้วค่อยลบวันที่ทิ้ง → ตัวเลือกนั้นใช้ไม่ได้แล้ว
  useEffect(() => {
    if (!withCustomerAvailable && answer === 'with_customer') {
      setAnswer(null);
    }
  }, [withCustomerAvailable, answer]);

  const handleConfirm = async () => {
    if (answer === null) {
      setError('กรุณาเลือกว่าของกลับมาแล้วหรือยังก่อนยืนยัน');
      return;
    }
    if (!deliveryDate && backfillInFuture) {
      setError('วันที่ส่งมอบต้องไม่เกินวันนี้ — แก้วันที่หรือเว้นว่างถ้ายังไม่ได้ส่งของ');
      return;
    }
    setLoading(true);
    try {
      await onConfirm({
        equipment_returned: answer === 'returned',
        equipment_condition: answer === 'returned' ? condition : undefined,
        // ส่งเฉพาะตอนที่ผู้ใช้กรอกใหม่จริง ๆ — สัญญาที่มีวันส่งมอบอยู่แล้วห้ามถูกเขียนทับ
        delivery_date: !deliveryDate && backfillDate ? backfillDate : undefined,
      });
      setAnswer(null);
      setCondition('GOOD');
      setBackfillDate('');
      setError('');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const optionClass = (active: boolean) =>
    cn(
      'w-full text-left border rounded-lg p-4 transition-colors',
      active ? 'border-primary-500 bg-primary-50' : 'border-secondary-200 hover:border-secondary-300'
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center">
          <RiAlertLine className="mr-2 h-6 w-6 text-danger-600" />
          ยกเลิกการเช่า - {rentalNumber}
        </span>
      }
      size="md"
    >
      <div className="p-6 space-y-4">
        <p className="text-sm text-secondary-700">
          สถานะจะเปลี่ยนเป็น "ยกเลิก" ทันที และแก้ไม่ได้
          {outstandingCount > 0 && (
            <> · มีอุปกรณ์ที่ระบบยังถือว่าอยู่กับลูกค้า <b>{outstandingCount} รายการ</b></>
          )}
        </p>

        {!deliveryDate && (
          <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 space-y-2">
            <div className="flex items-start gap-2 text-sm text-warning-800">
              <RiInformationLine className="h-5 w-5 flex-shrink-0 mt-0.5 text-warning-600" />
              <div>
                <b>สัญญานี้ยังไม่มีวันส่งมอบ</b> — ระบบจึงถือว่าอุปกรณ์ยังอยู่ในคลัง
                และจะปล่อยกลับเป็น "พร้อมใช้งาน" ทันทีที่ยกเลิก
                {canEditRental
                  ? ' · ถ้าส่งของให้ลูกค้าไปแล้ว ให้ระบุวันที่ส่งมอบก่อน ไม่งั้นอุปกรณ์จะถูกปล่อยให้เช่าซ้อน'
                  : ' · ถ้าส่งของให้ลูกค้าไปแล้ว ต้องให้ผู้ที่แก้ไขการเช่าได้ระบุวันที่ส่งมอบก่อนยกเลิก'}
              </div>
            </div>

            {canEditRental && (
              <div>
                <label
                  htmlFor="cancel-backfill-delivery-date"
                  className="block text-sm font-medium text-warning-900 mb-1"
                >
                  ส่งมอบให้ลูกค้าไปแล้วเมื่อ (ถ้ายังไม่ได้ส่ง ให้เว้นว่าง)
                </label>
                <input
                  id="cancel-backfill-delivery-date"
                  type="date"
                  value={backfillDate}
                  max={todayLocal}
                  aria-invalid={backfillInFuture || undefined}
                  onChange={(e) => { setBackfillDate(e.target.value); setError(''); }}
                  className="w-full border border-warning-300 rounded-lg px-3 py-2 bg-white"
                />
                {backfillInFuture && (
                  <p className="mt-1 text-xs text-danger-700" role="alert">
                    วันที่ส่งมอบต้องไม่เกินวันนี้
                  </p>
                )}
                <p className="mt-1 text-xs text-warning-700">
                  กรอกแล้วระบบจะบันทึกวันส่งมอบให้สัญญานี้ก่อน แล้วจึงยกเลิก
                  — ตัวเลือก "ของยังอยู่กับลูกค้า" จะใช้ได้จริงหลังจากนั้น
                </p>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            ตอนนี้ของอยู่ที่ไหน? <span className="text-danger-600">*</span>
          </label>
          <div className="space-y-2" data-name="goods_answer">
            <button
              type="button"
              className={optionClass(answer === 'returned')}
              onClick={() => { setAnswer('returned'); setError(''); }}
            >
              <div className="flex items-start">
                <RiCheckboxCircleLine className="h-5 w-5 mt-0.5 mr-2 text-success-600" />
                <div>
                  <div className="font-medium text-secondary-900">ของกลับมาครบแล้ว</div>
                  <div className="text-sm text-secondary-500">
                    → อุปกรณ์/ซิมกลับเข้าคลังทันที <b>พร้อมปล่อยให้เช่าต่อได้เลย</b>
                    <br />ระบบบันทึกรับคืนในใบตามเก็บให้เอง ไม่ต้องไปกดที่หน้าอื่น
                  </div>
                </div>
              </div>
            </button>

            {/* ปิดไว้เมื่อสัญญายังไม่มีวันส่งมอบ — เลือกได้ก็ไม่มีผล ระบบจะปล่อยของอยู่ดี
                (BUG-UI-08: ตัวเลือกที่ระบบไม่ได้เอาไปตัดสินใจ = คำโกหกบนจอ) */}
            <button
              type="button"
              disabled={!withCustomerAvailable}
              className={cn(
                optionClass(answer === 'with_customer'),
                !withCustomerAvailable && 'opacity-60 cursor-not-allowed hover:border-secondary-200'
              )}
              onClick={() => { setAnswer('with_customer'); setError(''); }}
            >
              <div className="flex items-start">
                <RiTruckLine className="h-5 w-5 mt-0.5 mr-2 text-warning-600" />
                <div>
                  <div className="font-medium text-secondary-900">ของยังอยู่กับลูกค้า ต้องไปเก็บ</div>
                  <div className="text-sm text-secondary-500">
                    → ระบบเปิด <b>ใบตามเก็บ</b> ให้ติดตาม · อุปกรณ์ <b>ยังไม่ว่าง</b> ให้เช่าต่อ
                    จนกว่าจะบันทึกรับคืน
                    <br />บันทึกรับคืนได้ที่หน้ารายละเอียดการเช่านี้ หรือเมนูติดตามการคืนอุปกรณ์
                  </div>
                  {!withCustomerAvailable && (
                    <div className="mt-1 text-sm font-medium text-warning-700">
                      เลือกไม่ได้จนกว่าสัญญาจะมีวันส่งมอบ
                      {canEditRental ? ' — ระบุวันที่ส่งมอบด้านบน' : ''}
                    </div>
                  )}
                </div>
              </div>
            </button>
          </div>
          {error && <p className="mt-2 text-sm text-danger-600">{error}</p>}
        </div>

        {answer === 'returned' && (
          <div>
            <label htmlFor="cancel-goods-condition" className="block text-sm font-medium text-secondary-700 mb-2">
              สภาพอุปกรณ์ที่รับคืน
            </label>
            <select
              id="cancel-goods-condition"
              value={condition}
              onChange={(e) => setCondition(e.target.value as GoodsCondition)}
              className="w-full border border-secondary-200 rounded-lg px-3 py-2"
            >
              {CONDITIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 px-6 py-4 border-t border-secondary-200">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50"
        >
          ปิด
        </button>
        <LoadingButton
          type="button"
          loading={loading}
          onClick={handleConfirm}
          className="px-4 py-2 text-sm rounded-lg bg-danger-600 text-white hover:bg-danger-700"
        >
          ยืนยันยกเลิกการเช่า
        </LoadingButton>
      </div>
    </Modal>
  );
};

export default CancelGoodsCheckModal;
